import { useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useStore } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import {
  CartSnapshotProperties,
  PetType,
  buildCartSnapshot,
  legacyAddedItemProperties,
} from '../lib/analytics/cartSnapshot';
import { CustomerIO } from '../lib/analytics/customerIO';
import {
  PendingAddedItemToCart,
  claimPendingAddedItemToCart,
  pendingAddsFromEarlierPages,
  savePendingAddedItemToCart,
} from '../lib/analytics/pendingAddedItemToCart';
import { CouponError, logInternalError } from '../lib/errors';
import { nextPricingOfCart, priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import { useActivePromotion } from './useActivePromoCode';

function reportTrackingError(err: unknown) {
  logInternalError(err instanceof Error ? err : new Error(String(err)), { tags: { source: 'trackAddedItemToCart' } });
}

/** how long to wait for the bag or in-app sheet to price the cart before pricing it here */
const PAGE_PRICING_WAIT_MS = 5000;

/**
 * The bag as the server prices it. Every add site routes to the bag or the in-app sheet, which price this same cart,
 * so the snapshot takes that response rather than sending a request of its own; only if none arrives in time is the
 * cart priced here. A code the server rejects is dropped, as the bag page drops it, so the snapshot does not
 * advertise it; any other failure leaves the snapshot with catalog figures only.
 */
async function priceForSnapshot(cart: types.StoreCart) {
  const pagePricing = await nextPricingOfCart(cart, PAGE_PRICING_WAIT_MS);
  if (pagePricing) {
    return { cart, cartPricing: pagePricing };
  }
  try {
    return { cart, cartPricing: await priceCartForPreview(cart) };
  } catch (err) {
    // already reported inside priceCartForPreview
    return { cart: err instanceof CouponError ? { ...cart, couponCode: undefined } : cart, cartPricing: undefined };
  }
}

/** Builds the snapshot once the bag is priced and sends it, if `claim` says this page is the one to send it. */
function useSendAddedItemToCart() {
  const store = useStore<types.AppState>();
  const { country, currency } = useLocale();
  const activePromotion = useActivePromotion();
  // read when the pricing arrives rather than when the wait began, as the bag starts waiting on its first render
  const latest = useRef({ country, currency, activePromotion });
  latest.current = { country, currency, activePromotion };

  return useCallback(
    async (added: Omit<PendingAddedItemToCart, 'page'>, claim: () => boolean) => {
      const { cart, cartPricing } = await priceForSnapshot(store.getState().storeShop.cart);
      if (!claim()) {
        return;
      }
      const { products } = store.getState().config;

      let snapshot: CartSnapshotProperties | undefined;
      try {
        snapshot = buildCartSnapshot({
          cart,
          products,
          cartPricing,
          ...latest.current,
          shopUrl: window.location.origin,
          now: new Date(),
          addedCartItemId: added.cartItemId,
          addedPetType: added.petType,
        });
      } catch (err) {
        // a snapshot bug degrades the event to the legacy fields below rather than dropping the trigger
        reportTrackingError(err);
      }
      try {
        CustomerIO.trackAddedItemToCart(snapshot ?? legacyAddedItemProperties(products, added.sku));
      } catch (err) {
        // analytics must never break the add; fire-and-forget callers do not handle a rejection
        reportTrackingError(err);
      }
    },
    [store],
  );
}

/**
 * Sends Customer.io's `Added Item To Cart` with a snapshot of the whole bag. Call it right after the
 * add is dispatched: the snapshot reads the bag from the store, so it includes the item just added.
 * The added item is the one holding `addedSku` that the bag did not have when the caller last rendered;
 * the reducer generates its id, so this is how the listing's species reaches that item alone. No new item
 * means the add went to another cart (the series 3+ PDP also fills the in-app upgrade cart) or did not
 * happen, and nothing is sent.
 *
 * Promo and multi-tracker prices are only known to the server, so the event waits for the page's own pricing of
 * the bag (see `priceForSnapshot`). If pricing fails the event goes out with catalog figures, and if the snapshot
 * cannot be built it goes out with the legacy `product_name` / `product_color` only, so the campaign is never
 * left untriggered. If the page is left for the bag by a full page load before the pricing arrives, the bag sends
 * it instead (see `useSendPendingAddedItemToCart`).
 */
export default function useTrackAddedItemToCart() {
  const store = useStore<types.AppState>();
  const send = useSendAddedItemToCart();
  const renderedCartItemIds = useSelector(
    (state: types.AppState) => Object.keys(state.storeShop.cart.cartItems),
    shallowEqual,
  );

  return useCallback(
    async (addedSku: string, addedPetType?: PetType) => {
      const addedItem = Object.values(store.getState().storeShop.cart.cartItems).find(
        (item) => item.lineItem.sku === addedSku && !renderedCartItemIds.includes(item.cartItemId),
      );
      if (!addedItem) {
        return;
      }
      const added = { cartItemId: addedItem.cartItemId, sku: addedSku, petType: addedPetType };
      const saved = savePendingAddedItemToCart(added);
      await send(added, () => !saved || claimPendingAddedItemToCart(added.cartItemId));
    },
    [store, send, renderedCartItemIds],
  );
}

/** On the bag, sends the adds an earlier page left before the pricing arrived there, once the bag is priced. */
export function useSendPendingAddedItemToCart() {
  const store = useStore<types.AppState>();
  const send = useSendAddedItemToCart();

  useEffect(() => {
    const { cartItems } = store.getState().storeShop.cart;
    pendingAddsFromEarlierPages().forEach((added) => {
      if (cartItems[added.cartItemId]) {
        void send(added, () => claimPendingAddedItemToCart(added.cartItemId));
      } else {
        // removed from the bag before it was sent
        claimPendingAddedItemToCart(added.cartItemId);
      }
    });
  }, [store, send]);
}
